import { Schemas } from "../types/api-types";
import apiClient from "../clients/apiClient";
import { ApiResponse } from "../schemas/apiResponseSchema";
import { Logger } from "../utils/logger";
import { getLoggerConfig } from "../config/logger.config";
import { JournalAccountRequest } from "../types/custom-types";

const logger = Logger.createContextLogger('TransactionService', getLoggerConfig());

export type TransactionResponse = Schemas['TransactionResponse'];
export type ReceiptRequest = Schemas['ReceiptRequest'];
export type GlTransactionHeader = Schemas['GlTransactionHeader'];
export type PlTransactionResponse = Schemas['PlTransactionResponse'];
export type WithdrawRequest = Schemas['WithdrawRequest'];
export type GlTransactionHeaderResponse = Schemas['GlTransactionHeaderResponse'];
export type JournalPrevMonthResponse = Schemas['JournalPrevMonthResponse'];
export type LoanDisbursementRequest = Schemas['LoanDisbursementRequest'];
// Prefer the precise alias from custom types which composes the two generated
// schemas (JournalAccountStandard | JournalAccountEquipment). This exists because
// the generator sometimes inlines the discriminated union and doesn't produce a
// top-level `JournalAccountRequest` key in `schemas`.
export type { JournalAccountRequest } from '../types/custom-types';
export type JournalRequest = Schemas['JournalRequest'];
export type GlTransactionListResponse = Schemas['GlTransactionListResponse'];
export type GlTransactionResponse = Schemas['GlTransactionResponse'];
export type ItTransactionTypeResponse = Schemas['ItTransactionTypeResponse'];
export type TransactionTypePrintConfig = Record<string, unknown>;
export type GLNotePolicyTResponse = Schemas['GLNotePolicyTResponse'];
export type GLMonthBalanceTResponse = Schemas['GLMonthBalanceTResponse'];
export type PlLoanAmotResponse = Schemas['PlLoanAmotResponse'];

// Fetch GL transactions with filters and pagination
export interface GlTransactionsFilter {
    page?: number;
    pageSize?: number;
    transactionType?: string;
    account?: string;
    dateFrom?: string;
    dateTo?: string;
    branch?: string; // For potential future use
    transactionNumber?: string;
}

export async function receipt(receiptRequest: ReceiptRequest): Promise<TransactionResponse> {
    try {
        const response: ApiResponse<TransactionResponse> = (
            await apiClient.post('/transaction/receipt',
                { ...receiptRequest }
            )).data;
        return response.data;

    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching customer failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getLastTransaction(transaction_type: number, paymentOnly?: boolean, isJournal?: boolean): Promise<GlTransactionHeader> {
    try {
        const params = new URLSearchParams();
        if (paymentOnly !== undefined) params.append('paymentOnly', paymentOnly.toString());
        if (isJournal !== undefined) params.append('isJournal', isJournal.toString());
        
        const queryString = params.toString();
        const url = `/transaction/last_transaction/${transaction_type}${queryString ? '?' + queryString : ''}`;
        
        const response: ApiResponse<GlTransactionHeader> = (
            await apiClient.get(url)
        ).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching last transaction failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getLastTransactionOfAccount(pl_account_id: number): Promise<PlTransactionResponse> {
    try {
        const response: ApiResponse<PlTransactionResponse> = (
            await apiClient.get(`/transaction/last_transaction/account/${pl_account_id}`)
        ).data;
        return response.data;
    }
    catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching last transaction failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getLastTransactionsListOfAccount(pl_account_id: number, limit: number): Promise<PlTransactionResponse[]> {
    try {
        const response: ApiResponse<PlTransactionResponse[]> = (
            await apiClient.get(`/transaction/last_transactions_list/account/${pl_account_id}?limit=${limit}`)
        ).data;
        return response.data;
    }
    catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching last transaction failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export interface PaginatedPlTransactions {
    items: PlTransactionResponse[];
    total_count: number;
    total_pages: number;
    current_page: number;
}

export async function updateAccountHold(plAccountId: number, minimumBalance: number): Promise<{ minimum_balance: number }> {
    try {
        const response: ApiResponse<{ minimum_balance: number }> = (
            await apiClient.put(`/loan-application/pl_account/${plAccountId}/hold`, { minimum_balance: minimumBalance })
        ).data;
        return response.data ?? { minimum_balance: minimumBalance };
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Failed to update hold amount');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getPaginatedTransactionsOfAccount(
    pl_account_id: number,
    page: number = 1,
    page_size: number = 20
): Promise<PaginatedPlTransactions> {
    try {
        const response: ApiResponse<PaginatedPlTransactions> = (
            await apiClient.get(`/transaction/transactions_list/account/${pl_account_id}`, {
                params: { page, page_size }
            })
        ).data;
        return response.data || { items: [], total_count: 0, total_pages: 1, current_page: 1 };
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching transactions failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function withdrawTransaction(data: WithdrawRequest): Promise<TransactionResponse> {
    try {
        const response: ApiResponse<TransactionResponse> = (
            await apiClient.post("/transaction/withdraw", data)
        ).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Withdraw transaction failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function payToCreditor(data: WithdrawRequest): Promise<TransactionResponse> {
    try {
        const response: ApiResponse<TransactionResponse> = (
            await apiClient.post("/transaction/pay-to-creditor", data)
        ).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Withdraw transaction failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getTransaction(transactionId: number): Promise<GlTransactionHeaderResponse> {
    try {
        const response: ApiResponse<GlTransactionHeaderResponse> = (await apiClient.get(`/transaction/gl_transaction_header/${transactionId}`)).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching transaction failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getPlTransaction(plTransactionId: number): Promise<PlTransactionResponse> {
    try {
        const response: ApiResponse<PlTransactionResponse> = (await apiClient.get(`/transaction/pl_transaction/${plTransactionId}`)).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching PL transaction failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getOpeningBalance(plAccountId: number): Promise<{ balance: number; passbook_number: number; updated_date?: string }> {
    try {
        const response: ApiResponse<{ balance: number; passbook_number: number; updated_date?: string }> = (await apiClient.get(`/transaction/${plAccountId}/opening-balance`)).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching opening balance failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getPlAccountData(plAccountId: number): Promise<PlTransactionResponse[]> {
    try {
        const response: ApiResponse<PlTransactionResponse[]> = (await apiClient.get(`/transaction/passbook_data/${plAccountId}`)).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching passbook data failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getPrintConfig(plAccountId: number, category?: string): Promise<any> {
    try {
        const url = category
            ? `/transaction/print_config/${plAccountId}?category=${category}`
            : `/transaction/print_config/${plAccountId}`;
        const response: ApiResponse<any> = (await apiClient.get(url)).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching print config failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getPassbookPolicy(passbookId: number): Promise<any> {
    try {
        const response: ApiResponse<any> = (await apiClient.get(`/transaction/passbook_policy/${passbookId}`)).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching passbook policy failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function issueNewPassbook(plAccountId: number, transactionIds: number[], startLine: number = 2): Promise<any> {
    try {
        const response: ApiResponse<any> = (await apiClient.post(`/transaction/pl_account/${plAccountId}/issue-new-passbook`, { 
            transaction_ids: transactionIds,
            start_line: startLine
        })).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Issue new passbook failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getPlTransactionsByHeaderAndAccount(transactionId: number, plAccountId: number): Promise<PlTransactionResponse> {
    try {
        const response: ApiResponse<PlTransactionResponse> = (await apiClient.get(`/transaction/pl_transaction_by_header/${transactionId}/and_pl_account/${plAccountId}`)).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching PL transactions failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getLastTransactionList(transactionTypeId: number, limit?: number, pl_only?: boolean) {
    try {
        const params = new URLSearchParams();
        if (limit) params.append('limit', limit.toString());
        if (pl_only !== undefined) params.append('pl_only', pl_only.toString());
        
        const queryString = params.toString();
        const url = `/transaction/last_transaction_headers_list/${transactionTypeId}${queryString ? '?' + queryString : ''}`;
        
        const response: ApiResponse<GlTransactionHeaderResponse[]> = (await apiClient.get(url)).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching last transactions failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function journalTransaction(journalRequest: JournalRequest): Promise<TransactionResponse> {
    try {
        const response: ApiResponse<TransactionResponse> = (
            await apiClient.post('/transaction/journal', journalRequest)
        ).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Journal transaction failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function saveJournalDraft(accounts: JournalAccountRequest[]): Promise<{ cached_accounts: number }> {
    try {
        const response: ApiResponse<{ cached_accounts: number }> = (
            await apiClient.post('/transaction/journal/draft', accounts)
        ).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Saving journal draft failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getJournalDraft(): Promise<JournalAccountRequest[]> {
    try {
        const response: ApiResponse<JournalAccountRequest[]> = (
            await apiClient.get('/transaction/journal/draft')
        ).data;
        return response.data || [];
    } catch (error: any) {
        // Don't throw on error - just return empty array
        logger.error('Failed to load journal draft', {}, error);
        return [];
    }
}

export async function clearJournalDraft(): Promise<{ deleted: boolean }> {
    try {
        const response: ApiResponse<{ deleted: boolean }> = (
            await apiClient.delete('/transaction/journal/draft')
        ).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Clearing journal draft failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getJournalTransactions(transactionType: number, limit?: number): Promise<GlTransactionHeaderResponse[]> {
    try {
        const params = new URLSearchParams();
        if (limit) {
            params.append('limit', limit.toString());
        }


        const url = `/transaction/last_transaction_headers_list/${transactionType}${params.toString() ? '?' + params.toString() : ''}`;
        const response: ApiResponse<GlTransactionHeaderResponse[]> = (
            await apiClient.get(url)
        ).data;
        return response.data || [];
    } catch (error: any) {
        logger.error('Failed to load journal transactions', {}, error);
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching journal transactions failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getJournalPrevMonth(branchId: number, options?: { skipAuthRedirect?: boolean }): Promise<JournalPrevMonthResponse> {
    try {
        const config = options?.skipAuthRedirect ? { headers: { 'x-skip-auth-redirect': '1' } } : undefined;
        const response: ApiResponse<JournalPrevMonthResponse> = (
            await apiClient.post('/transaction/journal/prev_month', { branch_id: branchId }, config)
        ).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching journal previous month status failed');
        }
        // Default to false on error to be safe
        logger.error('Failed to check journal prev month status', {}, error);
        return { show: false };
    }
}

export async function uploadFdCertificate(file: File, accountRefNumber: string): Promise<{ path: string }> {
    try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("account_ref_number", accountRefNumber);

        const response: ApiResponse<{ path: string }> = (
            await apiClient.post('/loan-application/fd_advance_upload_certificate', formData)
        ).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Certificate upload failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function fdAdvanceSave(data: any): Promise<TransactionResponse> {
    try {
        const response: ApiResponse<TransactionResponse> = (
            await apiClient.post('/loan-application/fd_advance_save', data)
        ).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            // Handle both APIResponse format and plain error format
            const detail = error.response.data.detail || error.response.data.message || 'FD Advance transaction failed';
            throw new Error(Array.isArray(detail) ? detail.map((d: any) => d.msg || d).join(', ') : detail);
        }
        throw new Error('network error, please contact support team');
    }
}

// Mobile POS Endpoints

export interface MobilePosAccountResponse {
    account_id: number;
    account_number: string;
    amount: number;
    status: number;
    date: string;
    customer_name?: string;
    account_name?: string;
    transaction_number?: string;
    transaction_id?: number;
}

export interface MobilePosBranchResponse {
    branch_id: number;
    branch_name: string;
    accounts: MobilePosAccountResponse[];
}

export interface MobilePosUiDataResponse {
    user_id: number;
    user_name: string;
    branches: MobilePosBranchResponse[];
}

export async function getMobilePosUiData(): Promise<MobilePosUiDataResponse[]> {
    try {
        const response: ApiResponse<MobilePosUiDataResponse[]> = (
            await apiClient.get('/transaction/mobile_pos_ui_data')
        ).data;
        return response.data || [];
    } catch (error: any) {
        logger.error('Failed to load mobile pos ui data', {}, error);
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching mobile pos ui data failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function deleteMobilePosTransaction(transactionId: number): Promise<boolean> {
    try {
        const response: ApiResponse<boolean> = (
            await apiClient.delete(`/transaction/mobile_pos/${transactionId}`)
        ).data;
        return response.data;
    } catch (error: any) {
        logger.error('Failed to delete mobile pos transaction', {}, error);
        if (error.response) {
            throw new Error(error.response.data.detail || 'Deleting mobile pos transaction failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function confirmMobilePosTransactions(requests: ReceiptRequest[]): Promise<{ count: number }> {
    try {
        const response: ApiResponse<{ count: number }> = (
            await apiClient.post('/transaction/mobile_pos', requests)
        ).data;
        return response.data;
    } catch (error: any) {
        logger.error('Failed to confirm mobile pos transactions', {}, error);
        if (error.response) {
            throw new Error(error.response.data.detail || 'Confirming mobile pos transactions failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function loanDisbursementTransaction(disbursementRequest: LoanDisbursementRequest): Promise<TransactionResponse> {
    try {
        const response: ApiResponse<TransactionResponse> = (
            await apiClient.post('/transaction/loan_disbursement',
                { ...disbursementRequest }
            )).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Loan disbursement transaction failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getGlTransactions(filters: GlTransactionsFilter): Promise<GlTransactionListResponse> {
    try {
        const params: Record<string, any> = {};
        if (filters.page) params.page = filters.page;
        if (filters.pageSize) params.page_size = filters.pageSize;
        if (filters.transactionType) params.transaction_type_id = filters.transactionType;
        if (filters.account) params.gl_coa_id = filters.account;
        if (filters.dateFrom) params.date_from = filters.dateFrom;
        if (filters.dateTo) params.date_to = filters.dateTo;
        if (filters.branch) params.branch_id = filters.branch; // For potential future use
        if (filters.transactionNumber) params.transaction_number = filters.transactionNumber;

        const response: ApiResponse<GlTransactionListResponse> = (
            await apiClient.get('/transaction/gl_transactions', { params })
        ).data;
        return response.data;
    } catch (error: any) {
        if (error.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error('Failed to fetch GL transactions');
    }
}

export async function listTransactionTypes(): Promise<ItTransactionTypeResponse[]> {
    try {
        const response: ApiResponse<ItTransactionTypeResponse[]> = (
            await apiClient.get('/transaction/types')
        ).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching transaction types failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getTrialBalance(branchId?: number, fromMonthId?: number, toMonthId?: number): Promise<GLNotePolicyTResponse[]> {
    try {
        const params = new URLSearchParams();
        if (branchId) params.append('branch_id', branchId.toString());
        if (fromMonthId) params.append('from_month_id', fromMonthId.toString());
        if (toMonthId) params.append('to_month_id', toMonthId.toString());
        const response: ApiResponse<GLNotePolicyTResponse[]> = (
            await apiClient.get(`/transaction/gl_month_balances?${params.toString()}`)
        ).data;
        return response.data || [];
    } catch (error: any) {
        logger.error('Failed to load trial balance data', { branchId, fromMonthId, toMonthId }, error);
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching trial balance failed');
        }
        throw new Error('network error, please contact support team');
    }
}




export interface ReverseJournalResponse {
    account_id: number;
    account_number: string;
    transaction_id: number;
    transaction_number: string;
    transaction_date: string;
    amount: number;
    interest: number;
    interest_benefit_max_int?: number | null;
}

export interface ReverseJournalUpdateRequest {
    transaction_id: number;
    interest_benefit_amount: number;
}

export async function getReverseJournalTransactions(customerId: number): Promise<ReverseJournalResponse[]> {
    try {
        const response: ApiResponse<ReverseJournalResponse[]> = (
            await apiClient.get(`/transaction/reverse_journal/${customerId}`)
        ).data;
        return response.data;
    } catch (error: any) {
        logger.error('Failed to get reverse journal transactions', {}, error);
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching reverse journal transactions failed');
        }
        throw new Error('network error, please contact support team');
    }
}


export async function updateReverseJournal(updates: ReverseJournalUpdateRequest[]): Promise<boolean> {
    try {
        const response: ApiResponse<boolean> = (
            await apiClient.post('/transaction/reverse_journal/update', updates)
        ).data;
        return response.data;
    } catch (error: any) {
        logger.error('Failed to update reverse journal', {}, error);
        if (error.response) {
            throw new Error(error.response.data.detail || 'Updating reverse journal failed');
        }
        throw new Error('network error, please contact support team');
    }
}

// Cancel Transaction types and functions
export interface CancellableTransactionResponse {
    id: number;
    transaction_number: string;
    date: string;
    amount: number;
    transaction_type: string;
}

export interface GetCancellableTransactionsFilter {
    onlyToday?: boolean;
    includeReceipts?: boolean;
    includeWithdrawals?: boolean;
    includePayments?: boolean;
}

export async function getCancellableTransactions(
    filters: GetCancellableTransactionsFilter
): Promise<CancellableTransactionResponse[]> {
    try {
        const params = new URLSearchParams();
        if (filters.onlyToday !== undefined) {
            params.append('only_today', String(filters.onlyToday));
        }
        if (filters.includeReceipts !== undefined) {
            params.append('include_receipts', String(filters.includeReceipts));
        }
        if (filters.includeWithdrawals !== undefined) {
            params.append('include_withdrawals', String(filters.includeWithdrawals));
        }
        if (filters.includePayments !== undefined) {
            params.append('include_payments', String(filters.includePayments));
        }

        const response: ApiResponse<CancellableTransactionResponse[]> = (
            await apiClient.get(`/transaction/cancellable?${params.toString()}`)
        ).data;
        return response.data;
    } catch (error: any) {
        logger.error('Failed to get cancellable transactions', {}, error);
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching cancellable transactions failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function uploadCancellationAttachment(file: File, transactionNumber: string): Promise<{ path: string }> {
    try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("transaction_number", transactionNumber);

        const response: ApiResponse<{ path: string }> = (
            await apiClient.post('/transaction/upload_cancellation_attachment', formData)
        ).data;
        return response.data;
    } catch (error: any) {
        logger.error('Failed to upload cancellation attachment', { transactionNumber }, error);
        if (error.response) {
            throw new Error(error.response.data.detail || 'Attachment upload failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function cancelTransaction(transactionNumber: string, ref1?: string): Promise<ApiResponse<any>> {
    try {
        const payload: any = { transaction_number: transactionNumber };
        if (ref1) {
            payload.ref1 = ref1;
        }

        const response: ApiResponse<any> = (
            await apiClient.post('/transaction/cancel', payload)
        ).data;
        return response;
    } catch (error: any) {
        logger.error('Failed to cancel transaction', { transactionNumber }, error);
        if (error.response) {
            throw new Error(error.response.data.detail || 'Cancelling transaction failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getPlLoanAmortization(plAccountId: number): Promise<PlLoanAmotResponse[]> {
    try {
        const response: ApiResponse<PlLoanAmotResponse[]> = (
            await apiClient.get(`/transaction/loan_amot/${plAccountId}`)
        ).data;
        return response.data || [];
    } catch (error: any) {
        logger.error('Failed to fetch loan amortization data', { plAccountId }, error);
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching loan amortization data failed');
        }
        throw new Error('network error, please contact support team');
    }
}

// Auto Recovery Types
export interface AutoRecoveryLoanDetail {
    loan_no: string;
    loan_account_id: number;
    capital: number;
    balance: number;
    interest: number;
    penalty: number;
    fee: number;
    fund: number;
    passdue: number;
    issue_on?: string;
    payment: number;
}

export interface AutoRecoveryRow {
    customer_no: string;
    customer_id: number;
    name: string;
    account_no: string;
    savings_account_id: number;
    savings_balance: number;
    total_deduction: number;
    balance_after_deduction: number;
    no_of_loan: number;
    loans: AutoRecoveryLoanDetail[];
}

export async function getAutoRecoveryData(limit?: number, offset: number = 0): Promise<AutoRecoveryRow[]> {
    try {
        const params = new URLSearchParams();
        if (limit !== undefined && limit !== null) {
            params.append('limit', limit.toString());
        }
        if (offset > 0) {
            params.append('offset', offset.toString());
        }

        const queryString = params.toString() ? `?${params.toString()}` : '';
        const response: ApiResponse<AutoRecoveryRow[]> = (
            await apiClient.get(`/transaction/auto_recovery/recoverable${queryString}`)
        ).data;
        return response.data || [];
    } catch (error: any) {
        logger.error('Failed to fetch auto recovery data', {}, error);
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching auto recovery data failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function confirmAutoRecoveryBatch(): Promise<{ status: string }> {
    try {
        const response: ApiResponse<{ status: string }> = (
            await apiClient.post('/transaction/auto_recovery/confirm')
        ).data;
        return response.data;
    } catch (error: any) {
        logger.error('Failed to confirm auto recovery batch', {}, error);
        if (error.response) {
            throw new Error(error.response.data.detail || 'Confirming auto recovery batch failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function updateAutoRecoveryBatch(recoveryData: AutoRecoveryRow[]): Promise<TransactionResponse> {
    try {
        const response: ApiResponse<TransactionResponse> = (
            await apiClient.post('/transaction/auto_recovery/update_batch', {
                recovery_data: recoveryData
            })
        ).data;
        return response.data;
    } catch (error: any) {
        logger.error('Failed to update auto recovery batch', {}, error);
        if (error.response) {
            throw new Error(error.response.data.detail || 'Updating auto recovery batch failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getAllTransactionTypes(): Promise<ItTransactionTypeResponse[]> {
    try {
        const response: ApiResponse<ItTransactionTypeResponse[]> = (
            await apiClient.get('/transaction/transaction-types')
        ).data;
        return response.data || [];
    } catch (error: any) {
        logger.error('Failed to fetch transaction types', {}, error);
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching transaction types failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getTransactionTypePrintConfig(typeId: number): Promise<TransactionTypePrintConfig | null> {
    try {
        const response: ApiResponse<TransactionTypePrintConfig | null> = (
            await apiClient.get(`/transaction/transaction-type-print/${typeId}`)
        ).data;
        return response.data ?? null;
    } catch (error: any) {
        logger.error('Failed to fetch transaction type print config', { typeId }, error);
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching transaction type print config failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function saveTransactionTypePrintConfig(
    typeId: number,
    config: TransactionTypePrintConfig
): Promise<TransactionTypePrintConfig> {
    try {
        const response: ApiResponse<TransactionTypePrintConfig> = (
            await apiClient.put(`/transaction/transaction-type-print/${typeId}`, config)
        ).data;
        return response.data || config;
    } catch (error: any) {
        logger.error('Failed to save transaction type print config', { typeId }, error);
        if (error.response) {
            throw new Error(error.response.data.detail || 'Saving transaction type print config failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function markTransactionsAsPrinted(transactionIds: number[]): Promise<any> {
    try {
        const response: ApiResponse<any> = (
            await apiClient.post('/pl-print-policy/mark-as-printed', transactionIds)
        ).data;
        return response.data;
    } catch (error: any) {
        logger.error('Failed to mark transactions as printed', { transactionIds: transactionIds.join(',') }, error);
        if (error.response) {
            throw new Error(error.response.data.detail || 'Marking transactions as printed failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function updateReceiptPrintStatus(transactionHeaderId: number): Promise<boolean> {
    try {
        const response: ApiResponse<boolean> = (
            await apiClient.patch(`/transaction/${transactionHeaderId}/receipt-print-status`)
        ).data;
        return response.data;
    } catch (error: any) {
        logger.error('Failed to update receipt print status', { transactionHeaderId }, error);
        if (error.response) {
            throw new Error(error.response.data.detail || 'Updating receipt print status failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export interface MobilePlTransactionResponse {
    id: number;
    pl_account_id: number;
    transaction_header_id: number;
    entered_date?: string;
    debit?: number;
    credit?: number;
    interest?: number;
    fee?: number;
    penalty?: number;
    balance?: number;
    description?: string;
    transaction_number?: string;
    account_number?: string;
    customer_name?: string;
    account_name_ln1?: string;
    account_name_ln2?: string;
    account_name_ln3?: string;
    entered_by_id?: number;
    entered_by_name?: string;
    branch_name?: string;
}

export interface MobilePlTransactionsFilter {
    entered_date?: string;
    branch_id?: number;
    transaction_number?: string;
    entered_by?: number;
}

export async function getMobilePlTransactions(filters: MobilePlTransactionsFilter): Promise<MobilePlTransactionResponse[]> {
    try {
        const params = new URLSearchParams();
        if (filters.entered_date) params.append('entered_date', filters.entered_date);
        if (filters.branch_id) params.append('branch_id', filters.branch_id.toString());
        if (filters.transaction_number) params.append('transaction_number', filters.transaction_number);
        if (filters.entered_by) params.append('entered_by', filters.entered_by.toString());

        const queryString = params.toString();
        const url = `/transaction/mobile_pl_transactions${queryString ? '?' + queryString : ''}`;

        const response: ApiResponse<MobilePlTransactionResponse[]> = (
            await apiClient.get(url)
        ).data;
        return response.data;
    } catch (error: any) {
        logger.error('Failed to fetch mobile PL transactions', {
            entered_date: filters.entered_date || '',
            branch_id: filters.branch_id || 0,
            transaction_number: filters.transaction_number || '',
            entered_by: filters.entered_by || 0
        }, error);
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching mobile transactions failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export interface CustomLoanRecoveryItem {
    loan_account_id: number;
    principal_amount: number;
    interest_amount: number;
}

export interface CustomLoanRecoveryRequest {
    customer_id: number;
    recovery_mode: 'saving' | 'current_account';
    savings_account_id?: number | null;
    cash_bank_gl_account_id?: number | null;
    cheque_num?: string | null;
    loans: CustomLoanRecoveryItem[];
    note?: string | null;
}

export async function processCustomLoanRecovery(request: CustomLoanRecoveryRequest): Promise<any> {
    const response = await apiClient.post('/transaction/custom_loan_recovery', request);
    return response.data.data;
}